"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { cn } from "@newxsystems/shared";
import type { KeyboardEvent } from "react";
import type { IKaajAccordionProps } from "./KaajAccordion.props";
import type { AccordionBlockItem } from "@newxsystems/types";

type PreparedItem = AccordionBlockItem & {
  itemId: string;
  triggerId: string;
  panelId: string;
};

const sanitizeIdPart = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "item";

const sizeClasses = {
  small: {
    item: "rounded-[18px]",
    trigger: "min-h-11 px-4 py-3 text-[0.95rem]",
    panel: "px-4 pb-4 pt-3 text-sm leading-6",
    indicator: "size-7 text-lg",
  },
  medium: {
    item: "rounded-[22px]",
    trigger: "min-h-12 px-5 py-4 text-base",
    panel: "px-5 pb-5 pt-4 text-[0.95rem] leading-7",
    indicator: "size-8 text-xl",
  },
  large: {
    item: "rounded-[24px] sm:rounded-[26px]",
    trigger: "min-h-14 px-4 py-4 text-base sm:px-6 sm:py-5 sm:text-lg",
    panel: "px-4 pb-6 pt-4 text-[0.98rem] leading-7 sm:px-6 sm:text-base sm:leading-8",
    indicator: "size-9 text-2xl",
  },
} as const;

const getFocusableButton = (button: HTMLButtonElement | null) =>
  button && !button.disabled ? button : null;

export const KaajAccordion = ({
  items,
  mode,
  direction,
  ariaLabel,
  presentation,
}: IKaajAccordionProps) => {
  const reactId = useId();
  const baseId = useMemo(() => sanitizeIdPart(`kaaj-accordion-${reactId}`), [reactId]);
  const triggerRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const classes = sizeClasses.medium;
  const allowMultipleOpen = mode === "multi";
  const isLedger = presentation === "ledger";

  const preparedItems = useMemo<PreparedItem[]>(() => {
    const usedIds = new Map<string, number>();

    return (items ?? []).map((item, index) => {
      const sourceId = sanitizeIdPart(`item-${index + 1}`);
      const count = usedIds.get(sourceId) ?? 0;
      usedIds.set(sourceId, count + 1);
      const itemId = count > 0 ? `${sourceId}-${count + 1}` : sourceId;
      const stableId = `${baseId}-${itemId}`;

      return {
        ...item,
        itemId,
        triggerId: `${stableId}-trigger`,
        panelId: `${stableId}-panel`,
      };
    });
  }, [baseId, items]);

  const initialOpenIds = useMemo(() => {
    const ids = preparedItems.filter((item) => item.defaultOpen).map((item) => item.itemId);
    return allowMultipleOpen ? ids : ids.slice(0, 1);
  }, [allowMultipleOpen, preparedItems]);

  const [openIds, setOpenIds] = useState<string[]>(initialOpenIds);

  useEffect(() => {
    setOpenIds(initialOpenIds);
  }, [initialOpenIds]);

  const toggleItem = (itemId: string) => {
    setOpenIds((current) => {
      const isOpen = current.includes(itemId);

      if (allowMultipleOpen) {
        return isOpen ? current.filter((id) => id !== itemId) : [...current, itemId];
      }

      return isOpen ? [] : [itemId];
    });
  };

  const focusButtonAt = (targetIndex: number) => {
    getFocusableButton(triggerRefs.current[targetIndex])?.focus();
  };

  const focusNextButton = (currentIndex: number, directionOffset: 1 | -1) => {
    for (let offset = 1; offset <= preparedItems.length; offset += 1) {
      const nextIndex =
        (currentIndex + directionOffset * offset + preparedItems.length) % preparedItems.length;

      if (getFocusableButton(triggerRefs.current[nextIndex])) {
        focusButtonAt(nextIndex);
        return;
      }
    }
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusNextButton(index, 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusNextButton(index, -1);
        break;
      case "Home":
        event.preventDefault();
        focusButtonAt(0);
        break;
      case "End":
        event.preventDefault();
        focusButtonAt(preparedItems.length - 1);
        break;
      default:
        break;
    }
  };

  if (preparedItems.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        isLedger
          ? "overflow-hidden rounded-[28px] border border-[#D9E1FF] bg-white shadow-[0_26px_76px_rgba(33,68,255,0.11)] sm:rounded-[32px]"
          : "space-y-3 sm:space-y-3.5",
      )}
      aria-label={ariaLabel}
      role="group"
      data-kaaj-accordion-direction={direction ?? undefined}
      data-kaaj-accordion-mode={mode ?? undefined}
      data-kaaj-accordion-presentation={presentation ?? undefined}
    >
      {preparedItems.map((item, index) => {
        const isOpen = openIds.includes(item.itemId);

        return (
          <div
            key={item.itemId}
            className={cn(
              "group/item relative isolate overflow-hidden transition-[border-color,background-color,box-shadow,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
              isLedger
                ? [
                    "border-0 border-t border-[#E6ECFF] first:border-t-0",
                    isOpen ? "bg-[#FAFBFF]" : "bg-white hover:bg-[#FCFDFF]",
                  ]
                : [
                    "border bg-white",
                    classes.item,
                    isOpen
                      ? "border-[#2144FF] bg-[#FAFBFF] shadow-[0_24px_68px_rgba(33,68,255,0.14)]"
                      : "border-[#D9E1FF] shadow-[0_14px_38px_rgba(33,68,255,0.07)] hover:border-[#B8C7FF] hover:bg-[#FCFDFF] hover:shadow-[0_20px_54px_rgba(33,68,255,0.11)] motion-safe:hover:-translate-y-0.5",
                  ],
            )}
          >
            <h3 className="m-0">
              <button
                ref={(node) => {
                  triggerRefs.current[index] = node;
                }}
                id={item.triggerId}
                type="button"
                className={cn(
                  "flex w-full cursor-pointer items-center justify-between gap-3 text-left font-bold leading-tight text-[#151927] transition-[background-color,color] duration-200 hover:bg-[#F4F6FF] active:bg-[#EEF2FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-2 focus-visible:ring-offset-white motion-reduce:transition-none sm:gap-4",
                  classes.trigger,
                )}
                aria-expanded={isOpen}
                aria-controls={item.panelId}
                onClick={() => toggleItem(item.itemId)}
                onKeyDown={(event) => handleTriggerKeyDown(event, index)}
              >
                <span className="block min-w-0 text-balance [overflow-wrap:anywhere]">
                  {item.heading}
                </span>
                <span
                  className={cn(
                    "relative flex shrink-0 items-center justify-center rounded-full border border-[#D9E1FF] bg-white text-[#2144FF] transition-[transform,border-color,background-color,box-shadow] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/item:border-[#B8C7FF] group-hover/item:bg-[#F6F8FF] motion-reduce:transition-none",
                    classes.indicator,
                    isOpen &&
                      "border-[#2144FF] bg-[#EEF0FF] shadow-[0_10px_24px_rgba(33,68,255,0.15)]",
                  )}
                  aria-hidden="true"
                >
                  <span className="absolute h-[2px] w-3 rounded-full bg-current transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none sm:w-3.5" />
                  <span
                    className={cn(
                      "absolute h-3 w-[2px] rounded-full bg-current transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none sm:h-3.5",
                      isOpen ? "scale-y-0 opacity-0" : "scale-y-100 opacity-100",
                    )}
                  />
                </span>
              </button>
            </h3>

            <div
              id={item.panelId}
              role="region"
              aria-labelledby={item.triggerId}
              aria-hidden={!isOpen}
              inert={!isOpen ? true : undefined}
              className={cn(
                "grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
                isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
              )}
            >
              <div className="min-h-0 overflow-hidden">
                <div
                  className={cn(
                    "border-t border-[#D9E1FF] text-pretty text-[#405076] [overflow-wrap:anywhere] [&>p]:m-0 [&>p+p]:mt-3 [&_a]:font-bold [&_a]:text-[#2144FF] [&_a]:underline [&_a]:decoration-[#2144FF]/25 [&_a]:underline-offset-4 hover:[&_a]:decoration-[#2144FF]",
                    classes.panel,
                  )}
                  dangerouslySetInnerHTML={{ __html: item.content }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

KaajAccordion.displayName = "Interactive::KaajAccordion";
